{**********************************************************************************
 * Copyright (C) 1990-2024 Systematic Marketing Ltd (SMXi Software)
 *
 * All code contained within this file is copyrighted and may only be used by
 * third parties with written permission from Systematic Marketing Ltd.
 *
 * For permissions, please contact us at: info@smxi.com
 * Visit our website for more information: https://smxi.com/codecopyright.html
 **********************************************************************************}

unit WebForm.Core;

interface

uses
  System.SysUtils,
  System.Classes,
  JS,
  Web,
  WEBLib.Graphics,
  WEBLib.Controls,
  WEBLib.Forms,
  WEBLib.Dialogs,
  Vcl.Controls,
  Vcl.StdCtrls,
  WEBLib.StdCtrls,
  Web.Core.Consts;

type
  TCoreWebForm = class(TForm)
  private
    { Private declarations }
    FOnSelfClose: TNotifyEvent;
  protected
    FPopUpForm: TForm;
    FFirstInvalidControl: string;
    procedure SelfClose;
    /// <summary>
    /// This calls the routine in MainForm
    /// </summary>
    /// <param name="AIcon">
    /// A FontAwesome icon such as 'fad fa-bug'
    /// </param>
    procedure ShowAToast(const ATitle, AMessage, AIcon: string);
    /// <summary>
    /// Calls the DisplayMessage method on the MainForm
    /// </summary>
    procedure DisplayMessage(const AMessage: string; DlgType: TMsgDlgType = mtInformation);
    /// <summary>
    /// Calls the ConfirmationRequest method on the MainForm
    /// </summary>
    [async]
    function ConfirmationRequest(const AMessage: string; DlgType: TMsgDlgType; Buttons: TMsgDlgButtons): TModalResult; async;
    [async]
    procedure ShowAPopUp(AFormClass: TFormClass); async;
    procedure ShowWaitMessage;
    procedure HideWaitMessage;
    function IsValid(const AElementId: string; const HighlightControls: Boolean): Boolean; virtual;
    function IsValidEx(const AElementId: string; const Highlight: THighlightOption): Boolean; virtual;
    procedure HighlightControl(const ThisElementId, TargetElementId: string; const AValid, Highlight: Boolean;
      const UseAltScheme: Boolean = False);
    procedure HighlightControlEx(const ThisElementId, TargetElementId: string; const AValid: Boolean; const Highlight:
      THighlightOption;
      const UseAltScheme: Boolean = False);
    procedure FocusOnFirstError;
    procedure UpdateDebugInfo;
    procedure LogToConsole(const Value: string);
    function GetPageCaption: string; virtual;
    /// <summary>
    /// Creates a TWebForm and renders it in a div with the specified ID
    /// </summary>
    /// <param name="AFormClass">The TWebForm class to instantiate</param>
    /// <param name="ADivId">The ID of the div where the form should be rendered</param>
    /// <param name="AFormInstance">Returns the created form instance (optional)</param>
    [async]
    procedure CreateAndRenderFormInDiv(AFormClass: TFormClass; const ADivId: string; out AFormInstance: TForm); async; overload;
    /// <summary>
    /// Creates a TWebForm and renders it in a div with the specified ID (simplified version)
    /// </summary>
    /// <param name="AFormClass">The TWebForm class to instantiate</param>
    /// <param name="ADivId">The ID of the div where the form should be rendered</param>
    [async]
    procedure CreateAndRenderFormInDiv(AFormClass: TFormClass; const ADivId: string); async; overload;
  public
    { Public declarations }
    [async]
    procedure InitForm; async; virtual;
    function CanClose: boolean; virtual;
    /// <summary>
    /// Finds a DOM element by id, searching within this form first and
    /// then falling back to the document.
    /// </summary>
    /// <param name="AId">The id attribute of the target element</param>
    /// <returns>TJSElement or nil if not found</returns>
    function FindElementById(const AId: string): TJSElement;
    procedure ClosePopUp; virtual;
    procedure TearDown; virtual;
    procedure AfterCreated; virtual;
    function PageHashName: string; virtual;
    property OnSelfClose: TNotifyEvent write FOnSelfClose;
  protected procedure LoadDFMValues; override; end;

implementation

{$R *.dfm}

uses
  System.StrUtils,
  smx.webcore.types,
//  {$IFDEF NOT_SMX_WEB}
  WebLib.WebTools,
//  {$ELSE}
//  MainDataModule, //don't think we need this
//  {$ENDIF}
  SMX.Web.Document.Utils;

{ TCoreWebForm }

procedure TCoreWebForm.AfterCreated;
begin

end;

function TCoreWebForm.CanClose: boolean;
begin
  Result := True;
end;

procedure TCoreWebForm.ClosePopUp;
begin
  if Assigned(FPopUpForm) then
  begin
    FPopUpForm.Close;
    FPopUpForm.Free;
    FPopUpForm := nil;
  end;
end;

function TCoreWebForm.ConfirmationRequest(const AMessage: string; DlgType: TMsgDlgType; Buttons: TMsgDlgButtons)
  : TModalResult;
begin
  {$IFNDEF NOT_SMX_WEB}
  Result := Await(TheMainForm.ConfirmationRequest(AMessage, DlgType, Buttons));
  {$ELSE}
  Result := mrNone;
  ShowMessage('Not supported in non smx web apps');
  {$ENDIF}
end;

procedure TCoreWebForm.DisplayMessage(const AMessage: string; DlgType: TMsgDlgType);
begin
  {$IFNDEF NOT_SMX_WEB}
  TheMainForm.DisplayMessage(AMessage, DlgType);
  {$ELSE}
  ShowMessage('Not supported in non smx web apps');
  {$ENDIF}
end;

procedure TCoreWebForm.FocusOnFirstError;
begin
  if FFirstInvalidControl <> '' then
    TDocUtils.focusElement(FFirstInvalidControl);
end;

function TCoreWebForm.GetPageCaption: string;
begin
  Result := 'An SMXi Application';
end;

procedure TCoreWebForm.HideWaitMessage;
begin
  {$IFNDEF NOT_SMX_WEB}
  TheMainForm.HideWaitMessage;
  {$ELSE}
  ShowMessage('Not supported in non smx web apps');
  {$ENDIF}
end;

procedure TCoreWebForm.HighlightControl(const ThisElementId, TargetElementId: string; const AValid, Highlight,
  UseAltScheme: Boolean);
var
  lValidityState: TValidityState;
begin
  if Highlight or (ThisElementId = TargetElementId) then
  begin
    if UseAltScheme then
      lValidityState := Alt_Valid_Check[AValid]
    else
      lValidityState := Valid_Check[AValid];
    TDocUtils.setControlValidity(ThisElementId, lValidityState);
  end;

  if (not AValid) and (FFirstInvalidControl = '') then
    FFirstInvalidControl := ThisElementId;
end;

procedure TCoreWebForm.HighlightControlEx(const ThisElementId, TargetElementId: string; const AValid: Boolean; const
  Highlight: THighlightOption;
  const UseAltScheme: Boolean);
var
  lValidityState: TValidityState;
begin
  if (Highlight = THighlightOption.hoAll) or ((Highlight = THighlightOption.hoSelected) and (ThisElementId =
    TargetElementId)) then
  begin
    if UseAltScheme then
      lValidityState := Alt_Valid_Check[AValid]
    else
      lValidityState := Valid_Check[AValid];
    TDocUtils.setControlValidity(ThisElementId, lValidityState);
  end;
end;

procedure TCoreWebForm.InitForm;
var
  lCaption: string;
begin
  if not Self.Popup then
  begin
    lCaption := GetPageCaption;
    if lCaption <> '' then
      Caption := GetPageCaption;
  end;
end;

function TCoreWebForm.IsValid(const AElementId: string; const HighlightControls: Boolean): Boolean;
begin
  FFirstInvalidControl := '';
  Result := True;
end;

function TCoreWebForm.IsValidEx(const AElementId: string; const Highlight: THighlightOption): Boolean;
begin
  Result := True;
end;

procedure TCoreWebForm.LogToConsole(const Value: string);
begin
  {$IFNDEF NOT_SMX_WEB}
  TheMainForm.LogToConsole(Value);
  {$ELSE}
  OutputDebugString(Value);
  {$ENDIF}

end;

function TCoreWebForm.PageHashName: string;
begin
  Result := Self.ClassName.Substring(1);
end;

procedure TCoreWebForm.SelfClose;
begin
  if Assigned(FOnSelfClose) then
    FOnSelfClose(Self);
end;

procedure TCoreWebForm.ShowAPopUp(AFormClass: TFormClass);
{$IFDEF DEBUG}
var
  lClass: string;
begin
  lClass := AFormClass.ClassName;
  {$ELSE}
begin
  {$ENDIF}
  FPopUpForm := AFormClass.Create(Self);
  try
    FPopUpForm.Popup := True;
    Await(TForm, FPopUpForm.Load());
    FPopUpForm.ElementClassName := POPUP_FORM_CLASS;
    FPopUpForm.PopupOpacity := 0.5;
    Await(TModalResult, FPopUpForm.Execute);
  finally
    FPopUpForm.Free;
    FPopUpForm := nil;
  end;
end;

procedure TCoreWebForm.ShowAToast(const ATitle, AMessage, AIcon: string);
begin
  {$IFNDEF NOT_SMX_WEB}
  TheMainForm.ShowAToast(ATitle, AMessage, AIcon);
  {$ELSE}
  ShowMessage('Not supported in non SMX Web apps');
  {$ENDIF}
end;

procedure TCoreWebForm.ShowWaitMessage;
begin
  {$IFNDEF NOT_SMX_WEB}
  TheMainForm.ShowWaitMessage;
  {$ELSE}
  ShowMessage('Not supported in non SMX Web apps');
  {$ENDIF}
end;

procedure TCoreWebForm.TearDown;
begin
  // for descendants
end;

procedure TCoreWebForm.UpdateDebugInfo;
begin
  {$IFNDEF NOT_SMX_WEB}
  {$IFDEF DEBUG}
  TheMainForm.UpdateDebugInfo;
  {$ENDIF}
  {$ELSE}
  ShowMessage('Not supported in non SMX Web apps');
  {$ENDIF}
end;

procedure TCoreWebForm.CreateAndRenderFormInDiv(AFormClass: TFormClass; const ADivId: string; out AFormInstance: TForm);
var
  lForm: TForm;
  lDivElement: TJSElement;
begin
  try
    // Create the form instance
    lForm := AFormClass.Create(Self);
    AFormInstance := lForm;
    
    // Set the form to not be a popup
    lForm.Popup := False;
    
    // Load the form
    Await(TForm, lForm.Load());
    
    // Get the target div element
    lDivElement := FindElementById(ADivId);
    
    if Assigned(lDivElement) then
    begin
      // Clear any existing content in the div
      lDivElement.innerHTML := '';
      
      // Append the form's element to the div
      lDivElement.appendChild(lForm.Element);
      
      // Initialize the form if it has an InitForm method
      if lForm is TCoreWebForm then
        TCoreWebForm(lForm).InitForm;
    end
    else
    begin
      raise Exception.Create('Div with ID "' + ADivId + '" not found');
    end;
  except
    on E: Exception do
    begin
      // Clean up on error
      if Assigned(AFormInstance) then
      begin
        AFormInstance.Free;
        AFormInstance := nil;
      end;
      raise;
    end;
  end;
end;

procedure TCoreWebForm.CreateAndRenderFormInDiv(AFormClass: TFormClass; const ADivId: string);
var
  lDummyForm: TForm;
begin
  // Call the main implementation with a dummy variable
  CreateAndRenderFormInDiv(AFormClass, ADivId, lDummyForm);
  // Note: The form will be owned by Self, so we don't need to manage lDummyForm
end;

function TCoreWebForm.FindElementById(const AId: string): TJSElement;
begin
  Result := nil;
  if AId = '' then
    Exit;

  // Prefer querying within this form's root element if available
  if Assigned(Self.Element) then
    Result := Self.Element.querySelector('#' + AId);

  // Fallback to document-level lookup
  if not Assigned(Result) then
    Result := document.getElementById(AId);
end;

procedure TCoreWebForm.LoadDFMValues;
begin
  inherited LoadDFMValues;


  try
    Name := 'CoreWebForm';
    Width := 640;
    Height := 480;
    CSSLibrary := cssBootstrap;
    ElementFont := efCSS;
    ElementPosition := epIgnore;
  finally
  end;
end;

end.

